import React, { useMemo, useReducer } from "react";
import { StepIndicator } from "@jsq/ds/core";
import {
  BasePayeeReducerContextProvider,
  PayeeReducerDispatchProvider,
  PayeeWorkflowContextProvider,
  PayeeWorkflowReducerDispatchProvider,
  initialPayeeState,
  initialWorkflowState,
  payeeReducer,
  payeeStepReducer,
} from "../Context/PayeeContext";
import { StepNames as EntityStepNames } from "../PayeeEntityWorkflow/constants";
import { EntityClientDetailsAndSupportingDocumentStep } from "../PayeeEntityWorkflow/StepComponents/EntityClientDetailsAndSupportingDocumentStep";
import { EntityPayeeDetailsStep } from "../PayeeEntityWorkflow/StepComponents/EntityPayeeDetailsStep";
import { EntityPaymentInstructionsStep } from "../PayeeEntityWorkflow/StepComponents/EntityPaymentInstructionsStep";
import { EntityReviewStep } from "../PayeeEntityWorkflow/StepComponents/EntityReviewStep";
import { StepNames as InvestmentStepNames } from "../PayeeInvestmentWorkflow/constants";
import { InvestmentClientDetailsAndSupportingDocumentStep } from "../PayeeInvestmentWorkflow/StepComponents/InvestmentClientDetailsAndSupportingDocumentStep";
import { InvestmentPayeeDetailsStep } from "../PayeeInvestmentWorkflow/StepComponents/InvestmentPayeeDetailsStep";
import { InvestmentPaymentInstructionsStep } from "../PayeeInvestmentWorkflow/StepComponents/InvestmentPaymentInstructionsStep";
import { InvestmentReviewStep } from "../PayeeInvestmentWorkflow/StepComponents/InvestmentReviewStep";
import { StepNames as VendorStepNames } from "../PayeeVendorWorkflow/constants";
import { VendorClientDetailsAndInvoiceStep } from "../PayeeVendorWorkflow/StepComponents/VendorClientDetailsAndInvoiceStep";
import { VendorPayeeDetailsStep } from "../PayeeVendorWorkflow/StepComponents/VendorPayeeDetailsStep";
import { VendorPaymentInstructionsStep } from "../PayeeVendorWorkflow/StepComponents/VendorPaymentInstructionsStep";
import { VendorReviewStep } from "../PayeeVendorWorkflow/StepComponents/VendorReviewStep";
import { PayeeWorkflowStep, PayeeBaseSupportingDocument } from "../types";
import { getHeaderStepStatus } from "../utils";

export type PayeeType = "Vendor" | "Entity" | "Investment";

export type UnifiedPayeeWorkflowProps = {
  payeeType: PayeeType;
  arenaId?: string;
  arenaName?: string;
  initialDocument?: PayeeBaseSupportingDocument;
  lockArena?: boolean;
  onPayeeCreateSuccess?: (createdPayee?: unknown) => void;
  onStepChange?: (stepData: {
    currentStep: number;
    totalSteps: number;
    canGoBack: boolean;
    canGoForward: boolean;
    isLoading: boolean;
    submitText: string;
  }) => void;
};

const SubmitForReviewText = "Submit for review";

export const UnifiedPayeeWorkflow: React.FC<UnifiedPayeeWorkflowProps> = ({
  payeeType,
  arenaId,
  arenaName,
  initialDocument,
  lockArena,
  onPayeeCreateSuccess,
  onStepChange,
}: UnifiedPayeeWorkflowProps) => {
  const baseInitialState =
    arenaId || arenaName ? { ...initialPayeeState, arenaId, arenaName } : initialPayeeState;
  const modalInitialState = initialDocument
    ? payeeType === "Vendor"
      ? { ...baseInitialState, invoices: [initialDocument] }
      : { ...baseInitialState, supportingDocuments: [initialDocument] }
    : baseInitialState;
  const [payeeData, dispatch] = useReducer(payeeReducer, modalInitialState);
  const [workflowData, dispatchWorkflow] = useReducer(payeeStepReducer, {
    ...initialWorkflowState,
    originalPayeeData: modalInitialState,
  });

  const workflowSteps: PayeeWorkflowStep[] = useMemo(() => {
    switch (payeeType) {
      case "Vendor":
        return [
          {
            index: 0,
            name: VendorStepNames.ClientDetailsAndInvoice,
            url: "0",
            status: getHeaderStepStatus(0, workflowData.stepNumber),
            component: () => <VendorClientDetailsAndInvoiceStep lockArena={lockArena} />,
          },
          {
            index: 1,
            name: VendorStepNames.PayeeDetails,
            url: "1",
            status: getHeaderStepStatus(1, workflowData.stepNumber),
            component: VendorPayeeDetailsStep,
          },
          {
            index: 2,
            name: VendorStepNames.PaymentInstructions,
            url: "2",
            status: getHeaderStepStatus(2, workflowData.stepNumber),
            component: VendorPaymentInstructionsStep,
          },
          {
            index: 3,
            name: VendorStepNames.Review,
            url: "3",
            status: getHeaderStepStatus(3, workflowData.stepNumber),
            component: () => <VendorReviewStep onPayeeCreateSuccess={onPayeeCreateSuccess} />,
          },
        ];
      case "Entity":
        return [
          {
            index: 0,
            name: EntityStepNames.ClientDetailsAndSupportingDocument,
            url: "0",
            status: getHeaderStepStatus(0, workflowData.stepNumber),
            component: () => (
              <EntityClientDetailsAndSupportingDocumentStep lockArena={lockArena} />
            ),
          },
          {
            index: 1,
            name: EntityStepNames.PayeeDetails,
            url: "1",
            status: getHeaderStepStatus(1, workflowData.stepNumber),
            component: EntityPayeeDetailsStep,
          },
          {
            index: 2,
            name: EntityStepNames.PaymentInstructions,
            url: "2",
            status: getHeaderStepStatus(2, workflowData.stepNumber),
            component: EntityPaymentInstructionsStep,
          },
          {
            index: 3,
            name: EntityStepNames.Review,
            url: "3",
            status: getHeaderStepStatus(3, workflowData.stepNumber),
            component: () => <EntityReviewStep onPayeeCreateSuccess={onPayeeCreateSuccess} />,
          },
        ];
      case "Investment":
        return [
          {
            index: 0,
            name: InvestmentStepNames.ClientDetailsAndSupportingDocument,
            url: "0",
            status: getHeaderStepStatus(0, workflowData.stepNumber),
            component: () => (
              <InvestmentClientDetailsAndSupportingDocumentStep lockArena={lockArena} />
            ),
          },
          {
            index: 1,
            name: InvestmentStepNames.PayeeDetails,
            url: "1",
            status: getHeaderStepStatus(1, workflowData.stepNumber),
            component: InvestmentPayeeDetailsStep,
          },
          {
            index: 2,
            name: InvestmentStepNames.PaymentInstructions,
            url: "2",
            status: getHeaderStepStatus(2, workflowData.stepNumber),
            component: InvestmentPaymentInstructionsStep,
          },
          {
            index: 3,
            name: InvestmentStepNames.Review,
            url: "3",
            status: getHeaderStepStatus(3, workflowData.stepNumber),
            component: () => (
              <InvestmentReviewStep onPayeeCreateSuccess={onPayeeCreateSuccess} />
            ),
          },
        ];
      default:
        return [];
    }
  }, [payeeType, workflowData, onPayeeCreateSuccess, lockArena]);

  const currentStepDetails = useMemo(
    () => workflowSteps[workflowData.stepNumber],
    [workflowData, workflowSteps],
  );

  const StepComponent = currentStepDetails?.component;

  React.useEffect(() => {
    if (onStepChange && workflowSteps.length > 0) {
      const isLastStep = workflowData.stepNumber === workflowSteps.length - 1;
      const submitText = isLastStep ? SubmitForReviewText : "Continue";

      onStepChange({
        currentStep: workflowData.stepNumber,
        totalSteps: workflowSteps.length,
        canGoBack: workflowData.stepNumber > 0,
        canGoForward: workflowData.stepNumber < workflowSteps.length - 1,
        isLoading: false,
        submitText,
      });
    }
  }, [workflowData.stepNumber, workflowSteps.length, onStepChange]);

  if (!StepComponent) {
    return null;
  }

  return (
    <BasePayeeReducerContextProvider value={payeeData}>
      <PayeeReducerDispatchProvider value={dispatch}>
        <PayeeWorkflowContextProvider value={workflowData}>
          <PayeeWorkflowReducerDispatchProvider value={dispatchWorkflow}>
            <StepIndicator steps={workflowSteps} disableStepLinks />
            <StepComponent />
          </PayeeWorkflowReducerDispatchProvider>
        </PayeeWorkflowContextProvider>
      </PayeeReducerDispatchProvider>
    </BasePayeeReducerContextProvider>
  );
};
